import { motion } from 'framer-motion';
import { Check, Users, MessageSquare, EyeOff, Camera, Eye, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

interface WizardProgressProps {
  currentStep: number;
  totalSteps: number;
  onStepClick?: (step: number) => void;
}

const stepLabels = {
  en: ['Start', 'Message', 'Identity', 'Photos', 'Preview', 'Send'],
  es: ['Inicio', 'Mensaje', 'Identidad', 'Fotos', 'Vista previa', 'Enviar'],
  fr: ['Début', 'Message', 'Identité', 'Photos', 'Aperçu', 'Envoyer'],
  pt: ['Início', 'Mensagem', 'Identidade', 'Fotos', 'Prévia', 'Enviar'],
  ar: ['البداية', 'الرسالة', 'الهوية', 'الصور', 'معاينة', 'إرسال'],
  ja: ['開始', 'メッセージ', '身元', '写真', 'プレビュー', '送信'],
  ko: ['시작', '메시지', '신원', '사진', '미리보기', '보내기'],
  id: ['Mulai', 'Pesan', 'Identitas', 'Foto', 'Pratinjau', 'Kirim'],
  hi: ['शुरू', 'संदेश', 'पहचान', 'फ़ोटो', 'पूर्वावलोकन', 'भेजें'],
};

const stepIcons = [Users, MessageSquare, EyeOff, Camera, Eye, Send];

export function WizardProgress({ currentStep, totalSteps, onStepClick }: WizardProgressProps) {
  const { language } = useLanguage();
  const labels = lc(stepLabels, language);

  return (
    <div className="w-full py-4">
      {/* Mobile: simple progress bar */}
      <div className="sm:hidden px-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-primary">{labels[currentStep - 1]}</span>
          <span className="text-xs text-muted-foreground">{currentStep}/{stepIcons.length}</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${(currentStep / stepIcons.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Desktop: step icons */}
      <div className="hidden sm:flex items-center justify-between max-w-xl mx-auto px-4">
        {stepIcons.map((Icon, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;

          return (
            <div key={index} className="flex items-center">
              <button
                onClick={() => onStepClick?.(stepNumber)}
                disabled={stepNumber > currentStep}
                className={cn(
                  'relative flex flex-col items-center gap-1.5 transition-all',
                  stepNumber <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'
                )}
              >
                <motion.div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all',
                    isCompleted && 'bg-primary border-primary text-primary-foreground',
                    isCurrent && 'border-primary bg-primary/10 text-primary',
                    !isCompleted && !isCurrent && 'border-muted-foreground/30 text-muted-foreground'
                  )}
                  initial={false}
                  animate={{ scale: isCurrent ? 1.1 : 1 }}
                >
                  {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </motion.div>
                <span className={cn(
                  'text-[10px] font-medium',
                  isCurrent && 'text-primary',
                  isCompleted && 'text-primary',
                  !isCompleted && !isCurrent && 'text-muted-foreground'
                )}>
                  {labels[index]}
                </span>
              </button>

              {index < stepIcons.length - 1 && (
                <div className="flex-1 mx-2">
                  <div className="h-0.5 bg-muted-foreground/20 relative w-8">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-primary"
                      initial={{ width: 0 }}
                      animate={{ width: isCompleted ? '100%' : '0%' }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
